import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useUriReachability } from './hooks/useUriReachability';
import ReachabilityIndicator from './components/ReachabilityIndicator';
import { useCallback, useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { AlertCircle, ExternalLink, Copy, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const DEFAULT_PROTOCOLS = ['https://', 'http://', 'rtsp://', 'rtsps://', 'rtmp://', 'ws://', 'wss://'];

export interface UriInputProps {
  field: ControllerRenderProps<FieldValues, string>;
  routeNamespaceId?: string;
  tenantId?: string;
  allowedProtocols?: string[];
  onValidationChange?: (isValid: boolean) => void;
  onValidationError?: (error: string | undefined) => void;
}

function parseUri(value: string): { protocol: string; path: string } {
  if (!value) {
    return { protocol: '', path: '' };
  }

  // Try to match known protocol patterns
  const protocolMatch = value.match(/^([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)/);
  if (protocolMatch) {
    return {
      protocol: protocolMatch[1].toLowerCase(),
      path: value.slice(protocolMatch[1].length),
    };
  }

  return { protocol: '', path: value };
}

function findMatchingProtocol(protocol: string, protocols: string[]): string | null {
  const normalizedSearch = protocol.toLowerCase();
  return protocols.find((p) => p.toLowerCase() === normalizedSearch) ?? null;
}

export default function UriInput({
  field,
  routeNamespaceId,
  tenantId,
  allowedProtocols,
  onValidationChange,
  onValidationError,
}: UriInputProps) {
  const protocols = allowedProtocols && allowedProtocols.length > 0 ? allowedProtocols : DEFAULT_PROTOCOLS;

  const { protocol: initialProtocol, path: initialPath } = parseUri(field.value || '');
  const matchedInitialProtocol = findMatchingProtocol(initialProtocol, protocols);

  const [selectedProtocol, setSelectedProtocol] = useState<string>(
    matchedInitialProtocol || protocols[0]
  );
  const [pathValue, setPathValue] = useState<string>(
    matchedInitialProtocol ? initialPath : (initialProtocol ? field.value || '' : initialPath)
  );
  const [showPasteHighlight, setShowPasteHighlight] = useState(false);

  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => clearTimeout(copiedTimeoutRef.current);
  }, []);

  const isHttpProtocol =
    selectedProtocol.toLowerCase().startsWith('http://') ||
    selectedProtocol.toLowerCase().startsWith('https://');

  const { status, result, checkReachability, cancel } = useUriReachability({
    routeNamespaceId,
    tenantId,
  });

  // Update form value when protocol or path changes
  const updateFormValue = useCallback(
    (protocol: string, path: string) => {
      const fullValue = path ? `${protocol}${path}` : '';
      field.onChange(fullValue);
    },
    [field]
  );

  // Sync from external changes to field.value
  useEffect(() => {
    const { protocol: newProtocol, path: newPath } = parseUri(field.value || '');
    const matched = findMatchingProtocol(newProtocol, protocols);

    if (matched) {
      if (matched !== selectedProtocol) {
        setSelectedProtocol(matched);
      }
      if (newPath !== pathValue) {
        setPathValue(newPath);
      }
    } else if (field.value !== `${selectedProtocol}${pathValue}`) {
      // Value changed externally and doesn't match our current state
      // This handles cases where the full value was set externally
      setPathValue(newProtocol ? field.value || '' : newPath);
    }
    // Only run when field.value changes externally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const handleProtocolChange = useCallback(
    (protocol: string) => {
      cancel();
      setSelectedProtocol(protocol);
      updateFormValue(protocol, pathValue);
    },
    [cancel, pathValue, updateFormValue]
  );

  const handlePathChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      cancel();
      const newValue = e.target.value;

      // Check if the user typed a protocol (e.g., "https://example.com")
      const { protocol: typedProtocol, path: extractedPath } = parseUri(newValue);
      if (typedProtocol) {
        const matchedProtocol = findMatchingProtocol(typedProtocol, protocols);
        if (matchedProtocol) {
          // Protocol detected and allowed - move to dropdown, keep rest in input
          setSelectedProtocol(matchedProtocol);
          setPathValue(extractedPath);
          updateFormValue(matchedProtocol, extractedPath);

          // Show highlight animation
          setShowPasteHighlight(true);
          setTimeout(() => setShowPasteHighlight(false), 600);
          return;
        }
      }

      // No protocol detected or not allowed - use value as-is
      setPathValue(newValue);
      updateFormValue(selectedProtocol, newValue);
    },
    [cancel, selectedProtocol, protocols, updateFormValue]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      const { protocol: pastedProtocol, path: pastedPath } = parseUri(pastedText);

      if (pastedProtocol) {
        const matchedProtocol = findMatchingProtocol(pastedProtocol, protocols);

        if (matchedProtocol) {
          // Protocol is allowed - auto-select and put rest in input
          e.preventDefault();
          cancel();
          setSelectedProtocol(matchedProtocol);
          setPathValue(pastedPath);
          updateFormValue(matchedProtocol, pastedPath);

          // Show highlight animation
          setShowPasteHighlight(true);
          setTimeout(() => setShowPasteHighlight(false), 600);
        }
        // If protocol not allowed, let default paste behavior happen
        // (full text goes into input, validation will fail)
      }
      // If no protocol in pasted text, let default paste behavior happen
    },
    [protocols, cancel, updateFormValue]
  );

  // Debounced auto-check: run reachability check 1s after user stops typing
  useEffect(() => {
    const fullValue = pathValue ? `${selectedProtocol}${pathValue}` : '';
    if (!fullValue) {
      return;
    }

    const timeoutId = setTimeout(() => {
      checkReachability(fullValue);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [selectedProtocol, pathValue, checkReachability]);

  // Notify parent of validation state changes
  useEffect(() => {
    const isValid = status !== 'invalid';
    onValidationChange?.(isValid);
    onValidationError?.(isValid ? undefined : 'Invalid URI format');
  }, [status, onValidationChange, onValidationError]);

  const handleBlur = useCallback(() => {
    field.onBlur();
  }, [field]);

  // When all text is selected (ctrl-A/cmd-A or double-click-select-all), include protocol in copy
  const handleCopy = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const input = inputRef.current;
      if (!input || !pathValue) return;

      const isAllSelected = input.selectionStart === 0 && input.selectionEnd === pathValue.length;
      if (isAllSelected) {
        e.preventDefault();
        const fullUri = `${selectedProtocol}${pathValue}`;
        e.clipboardData.setData('text/plain', fullUri);
      }
    },
    [selectedProtocol, pathValue]
  );

  const handleOpenInNewTab = useCallback(() => {
    if (!pathValue) return;
    const fullUri = `${selectedProtocol}${pathValue}`;
    window.open(fullUri, '_blank', 'noopener,noreferrer');
  }, [selectedProtocol, pathValue]);

  const handleCopyUri = useCallback(() => {
    if (!pathValue) return;
    const fullUri = `${selectedProtocol}${pathValue}`;
    navigator.clipboard.writeText(fullUri).catch(() => {});
    clearTimeout(copiedTimeoutRef.current);
    setCopied(true);
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }, [selectedProtocol, pathValue]);

  const hasMultipleProtocols = protocols.length > 1;
  const isInvalid = status === 'invalid';

  return (
    <div className="relative flex flex-1 items-stretch">
      {/* Protocol Dropdown/Label */}
      {hasMultipleProtocols ? (
        <Select value={selectedProtocol} onValueChange={handleProtocolChange}>
          <SelectTrigger
            className={cn(
              'w-auto shrink-0 gap-1 rounded-r-none border-r border-r-[#2E3835] bg-[#1a1a1a] pl-3 pr-2 [&>svg]:h-3.5 [&>svg]:w-3.5',
              showPasteHighlight && 'ring-2 ring-teal-400 ring-offset-0 transition-all duration-300',
              isInvalid && 'border-orange-500'
            )}
          >
            <span className="font-mono text-sm">{selectedProtocol}</span>
          </SelectTrigger>
          <SelectContent>
            {protocols.map((proto) => (
              <SelectItem key={proto} value={proto}>
                <span className="font-mono text-sm">{proto}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div
          className={cn(
            'flex shrink-0 items-center rounded-l-md border border-r-[#2E3835] bg-[#1a1a1a] px-3',
            showPasteHighlight && 'ring-2 ring-teal-400 ring-offset-0 transition-all duration-300',
            isInvalid && 'border-orange-500'
          )}
        >
          <span className="font-mono text-sm">{selectedProtocol}</span>
        </div>
      )}

      {/* Path Input */}
      <Input
        ref={inputRef}
        value={pathValue}
        onChange={handlePathChange}
        onPaste={handlePaste}
        onCopy={handleCopy}
        onBlur={handleBlur}
        autoComplete="off"
        className={cn(
          'flex-1 rounded-l-none border-l-0 pl-2',
          pathValue ? 'pr-16' : 'pr-9',
          isInvalid && 'border-orange-500 focus-visible:ring-orange-500'
        )}
      />

      {/* Action button + status indicator */}
      <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3">
        {/* Action button: open in new tab for HTTP(S), copy for others */}
        {pathValue &&
          (isHttpProtocol ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleOpenInNewTab}
                  className="cursor-pointer rounded p-0.5 text-muted-foreground transition-colors hover:text-lime-400"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Open in new tab</TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleCopyUri}
                  className="cursor-pointer rounded p-0.5 text-muted-foreground transition-colors hover:text-lime-400"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{copied ? 'Copied!' : 'Copy URI'}</TooltipContent>
            </Tooltip>
          ))}

        {/* Reachability Indicator (hidden when invalid - we show text error instead) */}
        {!isInvalid && <ReachabilityIndicator status={status} result={result} showPlaceholder={!!pathValue} />}

        {/* Invalid indicator icon */}
        {isInvalid && <AlertCircle className="h-4 w-4 text-orange-500" />}
      </div>
    </div>
  );
}
